import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { resolveOrgEntriesWithAutoHeal, normalizeOrganizationIdsFromUser } from '../utils/organizationAutoHeal';

export const OrgContext = createContext(null);

function resolveOrganizationIds(user) {
  return normalizeOrganizationIdsFromUser(user);
}

function readValidStoredOrgId(uid, organizationIds) {
  if (typeof localStorage === 'undefined') return null;
  const key = `zeropath_active_org_${uid}`;
  const stored = localStorage.getItem(key);
  if (!stored || !organizationIds.includes(stored)) return null;
  return stored;
}

export function OrgProvider({ user, children }) {
  const [isLoading, setIsLoading] = useState(!!user?.uid);
  const [organizationIds, setOrganizationIds] = useState([]);
  const [isMultiOrg, setIsMultiOrg] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [activeOrg, setActiveOrg] = useState(null);
  const [availableOrgs, setAvailableOrgs] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!user?.uid) {
        setOrganizationIds([]);
        setIsMultiOrg(false);
        setActiveOrgId(null);
        setActiveOrg(null);
        setAvailableOrgs([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const ids = resolveOrganizationIds(user);
      const multi = ids.length > 1;
      const storageKey = `zeropath_active_org_${user.uid}`;

      try {
        const { entries: validEntries } = await resolveOrgEntriesWithAutoHeal(
          ids,
          user
        );

        const validIds = validEntries.map((e) => e.orgId);
        const idListForStorage = validIds.length > 0 ? validIds : ids;
        const storedValid = readValidStoredOrgId(user.uid, idListForStorage);

        let nextActiveId = null;
        if (idListForStorage.length === 0) {
          nextActiveId = null;
        } else if (idListForStorage.length === 1) {
          nextActiveId = idListForStorage[0];
          try {
            localStorage.setItem(storageKey, nextActiveId);
          } catch (_) {
            /* ignore */
          }
        } else {
          nextActiveId = storedValid;
        }

        const av = validEntries.map(({ orgId, snap }) => ({
          id: orgId,
          name: snap.data().name || orgId
        }));

        let resolvedActiveId = null;
        if (validIds.length === 0) {
          resolvedActiveId = null;
        } else if (nextActiveId && validIds.includes(nextActiveId)) {
          resolvedActiveId = nextActiveId;
        } else if (storedValid && validIds.includes(storedValid)) {
          resolvedActiveId = storedValid;
        } else if (validIds.length === 1) {
          resolvedActiveId = validIds[0];
          try {
            localStorage.setItem(storageKey, resolvedActiveId);
          } catch (_) {
            /* ignore */
          }
        } else {
          resolvedActiveId = null;
        }

        let nextActiveOrg = null;
        if (resolvedActiveId) {
          const entry = validEntries.find((e) => e.orgId === resolvedActiveId);
          if (entry) {
            nextActiveOrg = { id: resolvedActiveId, ...entry.snap.data() };
          }
        }

        if (!cancelled) {
          setOrganizationIds(validIds);
          setIsMultiOrg(validIds.length > 1);
          setAvailableOrgs(av);
          setActiveOrgId(resolvedActiveId);
          setActiveOrg(nextActiveOrg);
        }
      } catch (e) {
        console.error('[OrgProvider] init failed:', e);
        if (!cancelled) {
          setOrganizationIds(ids);
          setIsMultiOrg(multi);
          setAvailableOrgs(ids.map((id) => ({ id, name: id })));
          setActiveOrgId(null);
          setActiveOrg(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setActiveOrgFn = useCallback(
    async (orgId) => {
      if (!user?.uid) return;
      if (!organizationIds.includes(orgId)) return;
      const storageKey = `zeropath_active_org_${user.uid}`;
      try {
        localStorage.setItem(storageKey, orgId);
      } catch (_) {
        /* ignore */
      }
      setActiveOrgId(orgId);
      try {
        const snap = await getDoc(doc(db, 'organizations', orgId));
        setActiveOrg(snap.exists() ? { id: orgId, ...snap.data() } : null);
      } catch {
        setActiveOrg(null);
      }
    },
    [user?.uid, organizationIds]
  );

  const hasOrganizationMembership = organizationIds.length > 0;

  const value = useMemo(
    () => ({
      activeOrgId,
      activeOrg,
      availableOrgs,
      organizationIds,
      isMultiOrg,
      hasOrganizationMembership,
      setActiveOrg: setActiveOrgFn,
      isLoading
    }),
    [
      activeOrgId,
      activeOrg,
      availableOrgs,
      organizationIds,
      isMultiOrg,
      hasOrganizationMembership,
      setActiveOrgFn,
      isLoading
    ]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
